import { useMemo } from "react";
import { useSettings } from "../contexts/SettingsContext";

const translations = {
  en: {
    "app.title": "Iraqi Railways Network Dashboard",
    "app.subtitle":
      "Monitor nationwide corridors, train allocations, and station activity with a modern command-center interface.",
    "app.lastUpdated": "Last updated",
    "app.refreshInterval": "Refresh every {value} seconds",
    "app.retry": "Retry",
    "filters.heading": "Active filters",
    "filters.none": "No filters applied",
    "filters.search": "Search: \"{query}\"",
    "filters.route": "Route: {label}",
    "filters.type": "Type: {label}",
    "filters.offlineHidden": "Offline trains hidden",
    "search.heading": "Explore the railway network",
    "search.placeholder": "Search by train code, station name, or city",
    "search.queryLabel": "Search",
    "search.routeLabel": "Route",
    "search.allRoutes": "All routes",
    "search.type.all": "All trains",
    "search.type.passenger": "Passenger",
    "search.type.freight": "Freight",
    "search.type.service": "Service",
    "stats.title": "National Rail Command Center",
    "stats.subtitle": "Real-time overview of routes, rolling stock, and station activity",
    "stats.routes": "Active routes",
    "stats.stations": "Stations monitored",
    "stats.trains": "Trains in registry",
    "stats.connectivity": "Fleet connectivity",
    "stats.online": "Online",
    "stats.offline": "Offline",
    "stats.stationPresence": "Station presence",
    "stats.withOffline": "Including offline units",
    "stats.withoutOffline": "Active units only",
    "stats.inStations": "at stations",
    "stats.outStations": "in transit",
    "stats.fleetComposition": "Fleet composition",
    "route.stations": "{count} stations",
    "route.trains": "{count} trains",
    "station.directory": "Station directory",
    "station.subtitle": "Key stops within the selected corridor",
    "station.locations": "{count} locations",
    "station.header.name": "Station",
    "station.header.route": "Route",
    "station.header.sequence": "Sequence",
    "trainBoard.empty": "No trains match the current filters. Try adjusting your search criteria.",
    "train.route": "Route · {name}",
    "train.location": "Current location",
    "train.journeyProgress": "Journey progress",
    "train.offline": "Offline",
    "train.online": "Online",
    "train.lastMessageDays": "{value} days",
    "train.unassignedRoute": "Unassigned",
    "train.lastStationLabel": "Last station",
    "train.lastStationTime": "Last message inside at {time}",
    "map.fullscreen": "Full screen",
    "map.title": "Network map",
    "map.transport": "Transport map",
    "map.standard": "Standard map",
    "map.filters": "Map filters",
    "map.hideOffline": "Hide offline trains",
    "map.drawLines": "Draw station links",
    "map.allRoutes": "All routes",
    "map.selectedRoutes": "Selected routes",
    "map.stationCount": "{count} trains",
    "settings.title": "User settings",
    "settings.language": "Interface language",
    "settings.mapLayer": "Default map layer",
    "settings.refresh": "Wialon refresh interval",
    "settings.offlineThreshold": "Offline detection threshold",
    "settings.drawLines": "Draw lines between stations",
    "settings.hideOffline": "Hide offline trains",
    "settings.hideTrainProgress": "Hide train progress",
    "settings.expand": "Expand settings",
    "settings.collapse": "Collapse settings",
    "settings.language.ar": "Arabic",
    "settings.language.en": "English",
    "settings.layer.transport": "Transport map",
    "settings.layer.standard": "Standard map",
    "settings.offlineHours": "{value} hours",
    "settings.offlineHour": "{value} hour",
    "settings.refreshSeconds": "{value} seconds",
    "settings.refreshSecond": "{value} second",
    "filters.selectedRoutes": "Routes: {label}",
    "filters.type.passenger": "Passenger",
    "filters.type.freight": "Freight",
    "filters.type.service": "Service",
  },
  ar: {
    "app.title": "لوحة شبكة سكك حديد العراق",
    "app.subtitle":
      "راقب الممرات الوطنية وتوزيع القطارات ونشاط المحطات عبر واجهة قيادة حديثة.",
    "app.lastUpdated": "آخر تحديث",
    "app.refreshInterval": "تحديث كل {value} ثانية",
    "app.retry": "إعادة المحاولة",
    "filters.heading": "عوامل التصفية النشطة",
    "filters.none": "لا توجد عوامل تصفية",
    "filters.search": "بحث: \"{query}\"",
    "filters.route": "الخط: {label}",
    "filters.type": "النوع: {label}",
    "filters.offlineHidden": "إخفاء القطارات غير المتصلة",
    "search.heading": "استكشف شبكة السكك الحديدية",
    "search.placeholder": "ابحث برقم القطار أو اسم المحطة أو المدينة",
    "search.queryLabel": "بحث",
    "search.routeLabel": "الخط",
    "search.allRoutes": "جميع الخطوط",
    "search.type.all": "كل القطارات",
    "search.type.passenger": "ركاب",
    "search.type.freight": "بضائع",
    "search.type.service": "خدمة",
    "stats.title": "مركز قيادة السكك الحديدية الوطني",
    "stats.subtitle": "نظرة لحظية على الخطوط والقطارات ونشاط المحطات",
    "stats.routes": "الخطوط النشطة",
    "stats.stations": "المحطات المراقبة",
    "stats.trains": "القطارات المسجلة",
    "stats.connectivity": "ارتباط الأسطول",
    "stats.online": "متصل",
    "stats.offline": "غير متصل",
    "stats.stationPresence": "التواجد في المحطات",
    "stats.withOffline": "بما في ذلك الوحدات غير المتصلة",
    "stats.withoutOffline": "الوحدات المتصلة فقط",
    "stats.inStations": "في المحطات",
    "stats.outStations": "في المسير",
    "stats.fleetComposition": "تركيبة الأسطول",
    "route.stations": "{count} محطة",
    "route.trains": "{count} قطار",
    "station.directory": "دليل المحطات",
    "station.subtitle": "أهم المحطات ضمن الممر المحدد",
    "station.locations": "{count} موقع",
    "station.header.name": "المحطة",
    "station.header.route": "الخط",
    "station.header.sequence": "الترتيب",
    "trainBoard.empty": "لا توجد قطارات مطابقة لعوامل التصفية الحالية. يرجى تعديل البحث.",
    "train.route": "الخط · {name}",
    "train.location": "الموقع الحالي",
    "train.journeyProgress": "تقدم الرحلة",
    "train.offline": "غير متصل",
    "train.online": "متصل",
    "train.lastMessageDays": "{value} يومًا",
    "train.unassignedRoute": "غير مخصص",
    "train.lastStationLabel": "آخر محطة",
    "train.lastStationTime": "آخر رسالة داخلها في {time}",
    "map.fullscreen": "عرض كامل",
    "map.title": "خريطة الشبكة",
    "map.transport": "خريطة النقل",
    "map.standard": "الخريطة القياسية",
    "map.filters": "عوامل تصفية الخريطة",
    "map.hideOffline": "إخفاء القطارات غير المتصلة",
    "map.drawLines": "إظهار خطوط المحطات",
    "map.allRoutes": "جميع الخطوط",
    "map.selectedRoutes": "الخطوط المحددة",
    "map.stationCount": "{count} قطارًا",
    "settings.title": "إعدادات المستخدم",
    "settings.language": "لغة الواجهة",
    "settings.mapLayer": "طبقة الخريطة الافتراضية",
    "settings.refresh": "معدل تحديث ويالون",
    "settings.offlineThreshold": "حد اكتشاف عدم الاتصال",
    "settings.drawLines": "رسم الخطوط بين المحطات",
    "settings.hideOffline": "إخفاء القطارات غير المتصلة",
    "settings.hideTrainProgress": "إخفاء تقدم القطار",
    "settings.expand": "عرض الإعدادات",
    "settings.collapse": "إخفاء الإعدادات",
    "settings.language.ar": "العربية",
    "settings.language.en": "الإنجليزية",
    "settings.layer.transport": "خريطة النقل",
    "settings.layer.standard": "الخريطة القياسية",
    "settings.offlineHours": "{value} ساعة",
    "settings.offlineHour": "{value} ساعة",
    "settings.refreshSeconds": "{value} ثانية",
    "settings.refreshSecond": "{value} ثانية",
    "filters.selectedRoutes": "الخطوط: {label}",
    "filters.type.passenger": "ركاب",
    "filters.type.freight": "بضائع",
    "filters.type.service": "خدمة",
  },
};

const routeNameMap = {
  startVertical: {
    en: "Southern Line – Umm Qasr",
    ar: "الخط الجنوبي - ام قصر",
  },
  southToNorth: {
    en: "Southern Line",
    ar: "الخط الجنوبي",
  },
  west: {
    en: "Western Line",
    ar: "الخط الغربي",
  },
  horizontalWest: {
    en: "Al-Qaim – Akashat Line",
    ar: "خط القائم - عكاشات",
  },
  angle: {
    en: "Baiji – Al-Haqlaniya Line",
    ar: "خط بيجي - الحقلانية",
  },
  est: {
    en: "Kirkuk – Baiji Line",
    ar: "خط كركوك - بيجي",
  },
};

const routeSubtitleMap = {
  startVertical: {
    en: "Industrial access to the southern ports",
    ar: "وصول صناعي إلى الموانئ الجنوبية",
  },
  southToNorth: {
    en: "Backbone service connecting Basra to Mosul",
    ar: "العمود الفقري الذي يربط البصرة بالموصل",
  },
  west: {
    en: "Express link toward Al-Qaim and the border",
    ar: "ربط سريع نحو القائم والحدود",
  },
  horizontalWest: {
    en: "Access to desert extraction sites",
    ar: "وصول إلى مواقع الاستخراج الصحراوية",
  },
  angle: {
    en: "Connector through rugged highlands",
    ar: "موصل عبر المرتفعات الوعرة",
  },
  est: {
    en: "Strategic petroleum distribution route",
    ar: "مسار استراتيجي لتوزيع النفط",
  },
};

const trainTypeMap = {
  passenger: {
    en: {
      label: "Passenger",
      description: "Local passenger service with frequent stops.",
    },
    ar: {
      label: "ركاب",
      description: "خدمة ركاب محلية بتوقفات متكررة.",
    },
  },
  freight: {
    en: {
      label: "Freight",
      description: "Heavy cargo and logistics operations.",
    },
    ar: {
      label: "بضائع",
      description: "عمليات شحن ولوجستيات ثقيلة.",
    },
  },
  fuel: {
    en: {
      label: "Fuel",
      description: "Petroleum and fuel transport units.",
    },
    ar: {
      label: "وقود",
      description: "وحدات نقل الوقود والمشتقات النفطية.",
    },
  },
  alignment: {
    en: {
      label: "Track alignment",
      description: "Maintenance units aligning railway bars.",
    },
    ar: {
      label: "ماكنات تحكيم",
      description: "وحدات صيانة لمحاذاة قضبان السكك الحديدية.",
    },
  },
  civil: {
    en: {
      label: "Civil works",
      description: "Support units for civil infrastructure projects.",
    },
    ar: {
      label: "اعمال هندسية مدنية",
      description: "وحدات دعم لمشاريع البنية التحتية المدنية.",
    },
  },
  other: {
    en: {
      label: "Other",
      description: "Unclassified rolling stock.",
    },
    ar: {
      label: "أخرى",
      description: "مركبات غير مصنفة.",
    },
  },
  service: {
    en: {
      label: "Other",
      description: "Unclassified rolling stock.",
    },
    ar: {
      label: "أخرى",
      description: "مركبات غير مصنفة.",
    },
  },
};

const mapLayerLabels = {
  transport: {
    en: "Transport map",
    ar: "خريطة النقل",
  },
  standard: {
    en: "Standard map",
    ar: "الخريطة القياسية",
  },
};

const format = (template, replacements = {}) =>
  template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match
  );

const resolveDictionary = (language) => translations[language] || translations.en;

const useTranslation = () => {
  const {
    settings: { language },
  } = useSettings();

  const dictionary = resolveDictionary(language);

  const t = useMemo(
    () =>
      (key, replacements) => {
        const template = dictionary[key] ?? translations.en[key] ?? key;
        return typeof template === "string" ? format(template, replacements) : key;
      },
    [dictionary]
  );

  const translateRouteName = useMemo(
    () =>
      (routeId, fallback) => {
        if (!routeId) {
          return fallback || "";
        }
        const entry = routeNameMap[routeId];
        if (!entry) {
          return fallback || routeId;
        }
        return entry[language] || entry.en || fallback || routeId;
      },
    [language]
  );

  const translateRouteSubtitle = useMemo(
    () =>
      (routeId, fallback) => {
        if (!routeId) {
          return fallback || "";
        }
        const entry = routeSubtitleMap[routeId];
        if (!entry) {
          return fallback || routeId;
        }
        return entry[language] || entry.en || fallback || routeId;
      },
    [language]
  );

  const translateTrainType = useMemo(
    () =>
      (typeKey, fallbackLabel) => {
        const entry = trainTypeMap[typeKey];
        if (!entry) {
          return {
            label: fallbackLabel || typeKey,
            description: "",
          };
        }

        const localized = entry[language] || entry.en || {};

        return {
          label: localized.label || fallbackLabel || typeKey,
          description: localized.description || "",
        };
      },
    [language]
  );

  const translateMapLayer = useMemo(
    () =>
      (layerKey) => {
        const entry = mapLayerLabels[layerKey];
        if (!entry) {
          return layerKey;
        }
        return entry[language] || entry.en || layerKey;
      },
    [language]
  );

  const direction = language === "ar" ? "rtl" : "ltr";

  return {
    language,
    direction,
    t,
    translateRouteName,
    translateRouteSubtitle,
    translateTrainType,
    translateMapLayer,
  };
};

export { useTranslation, translations, routeNameMap, routeSubtitleMap, trainTypeMap };
